import { useEffect, useState } from 'react';
import { GAMES_LIST_PAGE_SIZE } from '../../constants';
import { useGetLobby } from '../../store/selectors';
import { getGames } from '../../utils';
import { useOnLangPlatformChange } from './hooks/useOnLangPlatformChange';
import {
  useAdjustPaginationGamesToCurrentPageNumber,
  useGetPlexPaginationCommonData,
  usePlexPaginationHandlePageChange,
  usePlexPaginationLoadGames,
  usePlexPaginationResetPage,
  usePlexPaginationSetInitialData
} from './paginationHelperHooks';

export const usePlexProviderPagination = ({
  casinoType,
  provider,
  pageSize = GAMES_LIST_PAGE_SIZE,
  isCollections
}) => {
  // getting common data from contexts
  const { gamesData, feedName, plexPagination, userInfoLoaded, formattedPage } =
    useGetPlexPaginationCommonData(casinoType);

  const casinoLobby = useGetLobby(casinoType);

  const { isCategoriesLoaded, isProvidersLoaded } = casinoLobby;
  const isLobbyDataLoaded =
    isCategoriesLoaded && isProvidersLoaded && userInfoLoaded;

  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const totalGamesToLoad = casinoLobby.providersTotalGames?.[provider];
  const providerGames =
    plexPagination.provider?.id === provider
      ? plexPagination.provider?.gamesIds || []
      : undefined;

  // getting loadGames function
  const {
    loadGames: loadProviderGames,
    isLoading,
    setIsLoading
  } = usePlexPaginationLoadGames({
    entity: 'provider',
    entityId: provider,
    paginationGamesIds: plexPagination.provider?.gamesIds,
    casinoType,
    feedName
  });

  // Reset isInitialDataLoaded state after change language or platform
  useOnLangPlatformChange(() => setIsInitialDataLoaded(false));

  // reset state on provider change
  useEffect(() => {
    if (!provider || isCollections) {
      return;
    }

    if (plexPagination.provider?.id !== provider) {
      setIsInitialDataLoaded(false);
      setIsLoading(false);
    }
  }, [provider]);

  // redirect to first page if no pagination data for provider & page > 5
  usePlexPaginationResetPage({
    isNotAllowedToReset: !provider || isCollections,
    pageSize,
    games: providerGames
  });

  const isNotAllowedToSetInitialData =
    !isLobbyDataLoaded ||
    isLoading ||
    isInitialDataLoaded ||
    !provider ||
    isCollections;

  // set initial value
  usePlexPaginationSetInitialData({
    isNotAllowedToSetInitialData,
    pageSize,
    gamesFromLobby: casinoLobby?.providersGames?.[provider],
    paginationGames: providerGames,
    formattedPage,
    entity: 'provider',
    entityId: provider,
    setIsInitialDataLoaded,
    loadGames: loadProviderGames
  });

  useAdjustPaginationGamesToCurrentPageNumber({
    paginationGames: providerGames,
    formattedPage,
    pageSize,
    entity: 'provider',
    entityId: provider,
    adjustIsEnabled:
      provider && !isCollections && plexPagination.provider?.id === provider
  });

  const handlePageChange = usePlexPaginationHandlePageChange({
    loadGames: loadProviderGames,
    pageSize
  });

  return {
    page: formattedPage,
    handlePageChange,
    games: providerGames ? getGames(providerGames, gamesData) : providerGames,
    isAllGamesLoaded: providerGames?.length >= totalGamesToLoad,
    isLoading,
    isInitialDataLoaded
  };
};
